var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { badgeEntityName } from '../../../../backend/features/badges/shared';
import { BadgeOrganizationsEditDialog } from '../../../../ui/features/badge-management/components/BadgeList/BadgeOrganizationsEditDialog';
import { getBadgeDetailsFormConfig } from '../../../../ui/features/badge-management/utils/getBadgeDetailsFormConfig';
import { manageBadgesRoutes } from '../../../../ui/features/badge-management/utils/manageBadgesRoutes';
import { useBadgingTranslation } from '../../../../ui/hooks';
import { showFormDialog } from '@juvare/common-form-ui/showFormDialog';
import { showConfirmDialog, showCustomDialog } from '@juvare/common-web-ui/DialogHelper';
import { handleErrorSnackbar, showAlertSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { useStorageClient } from '@juvare/platform-sdk-react/ui/hooks';
import React from 'react';
import { useNavigate } from 'react-router-dom';
/**
 * Centralized place for all single Badge actions
 */
export const useBadgeActions = (props) => {
    const { t } = useBadgingTranslation();
    const navigate = useNavigate();
    const badgeStorageClient = useStorageClient(badgeEntityName);
    const { badge, onSuccess } = props;
    const handleRequestSuccess = React.useCallback((options) => {
        void onSuccess();
        showAlertSnackbar({
            horizontalPosition: 'center',
            type: 'success',
            verticalPosition: 'bottom',
            autoHideDuration: 2000,
            message: options.message,
        });
    }, [onSuccess]);
    const updateBadge = React.useCallback((data) => __awaiter(void 0, void 0, void 0, function* () {
        return badgeStorageClient
            .post({
            action: 'partial-update',
            id: badge.id,
            data,
        })
            .then(() => handleRequestSuccess({ message: t('badges.badgeList.editDialog.successMessage') }))
            .catch(handleErrorSnackbar);
    }), [badgeStorageClient, badge.id, handleRequestSuccess, t]);
    const handleBadgeDetailsEdit = React.useCallback(() => {
        showFormDialog({
            title: t('badges.badgeList.editDialog.title', { badgeDocumentName: badge.name }),
            config: getBadgeDetailsFormConfig(t),
            submitLabel: t('badges.badgeList.editDialog.submitLabel'),
            data: {
                badgeDocumentName: badge.name,
                badgeDescription: badge.description,
            },
            onSubmit: (data) => {
                void updateBadge({
                    name: data.badgeDocumentName,
                    description: data.badgeDescription,
                });
            },
        });
    }, [badge.description, badge.name, t, updateBadge]);
    const handleBadgeOrganizationsEdit = React.useCallback(() => {
        showCustomDialog({
            content: (close, ariaTitleId, ariaContentId) => {
                return (React.createElement(BadgeOrganizationsEditDialog, { ariaTitleId: ariaTitleId, ariaContentId: ariaContentId, badgeOrganizationIds: badge.organizationIds, onSubmit: (organizationIds) => {
                        void updateBadge({ organizationIds }).then(() => {
                            close();
                        });
                    }, onCancel: () => {
                        close();
                    } }));
            },
            onEscapeKeyDown: (close) => close(),
        });
    }, [badge.organizationIds, updateBadge]);
    const handleBadgeDelete = React.useCallback(() => {
        showConfirmDialog({
            title: t('badges.badgeList.deleteDialog.title', { badgeDocumentName: badge.name }),
            content: t('badges.badgeList.deleteDialog.description'),
            confirmLabel: t('common.confirm'),
            onConfirm: () => {
                badgeStorageClient
                    .post({
                    action: 'delete',
                    id: badge.id,
                })
                    .then(() => handleRequestSuccess({ message: t('badges.badgeList.deleteDialog.successMessage') }))
                    .catch(handleErrorSnackbar);
            },
        });
    }, [t, badge.name, badge.id, badgeStorageClient, handleRequestSuccess]);
    const handleBadgeClick = React.useCallback(() => {
        navigate(manageBadgesRoutes.badgeDetails(badge.id, 'assigned-accounts'));
    }, [badge.id, navigate]);
    const handleBadgeDuplicateClick = React.useCallback((routeOptions) => {
        navigate(manageBadgesRoutes.duplicateBadge(badge.id, routeOptions));
    }, [badge.id, navigate]);
    return {
        handleBadgeDetailsEdit,
        handleBadgeOrganizationsEdit,
        handleBadgeDelete,
        handleBadgeClick,
        handleBadgeDuplicateClick,
    };
};
